import { useEffect, useMemo, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useStore } from 'effector-react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { type GeneralID, SelectStatus } from '@types';
import TextLink from '@components/TextLink';
import Checkbox from '@components/Checkbox';
import getSelectedStatus from '@utils/getSelectedStatus';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { dragScenesToCategory } from '@modules/category/actions/dragActions';
import { showConfirmModal } from '@components/ConfirmModal';
import { deleteScene } from '@modules/category/actions/deleteActions';
import {
  sortCategoryScenes,
  sortSubCategoryScenes,
} from '@modules/category/actions/sortScenesActions';
import { showScenesCopyModal } from '@modules/category/ScenesCopyModal';
import { $scenesManagement, $scenesManagementApi } from '../stores';
import { SMDndType, type SMDragItem, isScenesItem } from '../dnd';
import { Item } from './Item';

export function MSceneList() {
  const { sceneList, selected, categoryPath } = useStore($scenesManagement);
  const [parent] = useAutoAnimate();
  const [activeId, setActiveId] = useState<GeneralID | undefined>();

  const categoryId = categoryPath[categoryPath.length - 1];

  const ids = useMemo(() => {
    if (!activeId) return [];
    if (selected.includes(activeId)) {
      return selected;
    }
    return [activeId];
  }, [activeId, selected]);

  const [{ isOver: isOverBody }, drop] = useDrop({
    accept: SMDndType.scenes,
    collect: (monitor) => {
      return {
        isOver: monitor.isOver({ shallow: true }),
      };
    },
    drop: (item: SMDragItem, monitor) => {
      if (isScenesItem(item, monitor.getItemType())) {
        if (monitor.isOver({ shallow: true })) {
          dragScenesToCategory({
            categoryId,
            ids: item.ids,
          });
        }
      }
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: SMDndType.scenes,
    item: {
      ids: ids.map((_id) => ({ id: _id, parentId: categoryId })),
      includeSelected: selected.includes(activeId!),
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: false });
  }, []);

  const handleDelete = async () => {
    const { confirmed } = await showConfirmModal({
      title: `确认删除${selected.length}个场景`,
      content: '所选场景下所有设置也都将会被删除，确认删除？',
    });
    if (confirmed) {
      deleteScene(
        selected.map((_id) => ({
          sceneId: _id,
          categoryId,
        }))
      );
      $scenesManagementApi.update((draft) => {
        draft.selected = [];
      });
    }
  };

  const handleSort = () => {
    if (categoryPath.length > 1) {
      sortSubCategoryScenes(categoryId);
    } else {
      sortCategoryScenes(categoryId);
    }
  };

  const list = isDragging
    ? sceneList.filter((id) => !ids.includes(id))
    : sceneList;

  return (
    <div className="relative grid grid-rows-[50px_1fr] border-l">
      <div className="relative flex items-center bg-[#f8f8f8] px-20">
        <Checkbox
          label="全选"
          status={getSelectedStatus(selected.length, sceneList.length)}
          onClick={(status) => {
            if (status === SelectStatus.all) {
              $scenesManagementApi.update((draft) => {
                draft.selected = [];
              });
            } else {
              $scenesManagementApi.update((draft) => {
                draft.selected = [...sceneList];
              });
            }
          }}
        />
        <div className="ml-auto flex items-center gap-20">
          <TextLink
            disabled={!selected.length}
            onClick={() => {
              showScenesCopyModal({ level: '场景', sceneIds: selected });
            }}
          >
            跨作品复制
          </TextLink>
          <TextLink disabled={!selected.length} onClick={handleDelete}>
            批量删除
          </TextLink>
          <TextLink disabled={!sceneList.length} onClick={handleSort}>
            一键排序
          </TextLink>
        </div>
      </div>
      {sceneList.length ? (
        <div ref={drop}>
          <Scrollbars>
            <div className="relative" ref={drag}>
              <div ref={parent}>
                {list.map((id) => {
                  return (
                    <Item
                      key={id}
                      id={id}
                      isDragging={isDragging}
                      categoryId={categoryId}
                      onActive={setActiveId}
                    />
                  );
                })}
                {isOverBody && (
                  <div className="absolute -bottom-5 left-0 right-0 z-50 h-4 bg-green/50" />
                )}
              </div>
            </div>
          </Scrollbars>
        </div>
      ) : (
        <div className="flex items-center justify-center">
          <div className="text-lg text-black-gray">暂无场景</div>
        </div>
      )}
    </div>
  );
}

export default MSceneList;
